import * as React from "react";
import { useState } from "react";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { IconBaseProps } from "react-icons";

import { cn } from "../lib/utils";
import { Label } from "./ui/label";

interface InputTextProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ComponentType<IconBaseProps>;
  error?: string;
  title?: string;
  showToggle?: boolean;
}

const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
  ({ error, title, required, name, type = "text", className, icon: Icon, showToggle, ...rest }, ref) => {
    const [showPass, setShowPass] = useState(false);
    const inputId = name || `input-text-${Math.random().toString(36).substr(2, 9)}`;
    const resolvedType = showToggle && type === "password" ? (showPass ? "text" : "password") : type;

    return (
      <div className="space-y-2">
        {title && (
          <Label
            htmlFor={inputId}
            className={cn(
              "text-sm font-medium text-muted-foreground",
              error && "text-red-600 dark:text-red-400"
            )}
          >
            {title}
            {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
          </Label>
        )}
        <div className="relative">
          {Icon && (
            <Icon
              className={cn(
                "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2",
                error
                  ? "text-red-400"
                  : "text-primary"
              )}
              size={16}
            />
          )}
          <input
            id={inputId}
            name={name}
            type={resolvedType}
            ref={ref}
            className={cn(
              "flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              Icon && "pl-10",
              showToggle && "pr-10",
              error && "border-red-300 dark:border-red-800 focus-visible:ring-red-500",
              className
            )}
            required={required}
            {...rest}
          />
          {showToggle && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPass((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);

InputText.displayName = "InputText";

export { InputText };
